<template>
  <Head :title="`${t('page.title')} - ${course.code}`" />
  <s-page-block>
    <s-page-header :heading="t('page.title')" class="mb-3" />
    <search>
      <div class="relative group mb-3">
        <s-input-field
          id="searchText"
          :placeholder="t('search.placeholder')"
          v-model="form.search"
          autocomplete="off"
          size="sm"
          class="!rounded-full !pl-4 !pr-10"
          :aria-label="t('search.placeholder')"
        />
        <s-icon
          name="magnify"
          class="absolute top-1/2 -translate-y-1/2 right-4 text-blue-500 opacity-70 z-10"
          size="20"
          aria-hidden="true"
        />
      </div>
      <div class="mb-4 flex flex-wrap gap-x-4 gap-y-3 justify-between items-center">
        <s-filter-group>
          <s-date-range-filter
            v-model="form.startDate"
            label-style="after-before"
            :label="t('placeholders.startDate')"
          />
          <s-date-range-filter
            v-model="form.dueDate"
            label-style="after-before"
            :label="t('placeholders.dueDate')"
          />
          <s-select-filter
            v-model="form.status"
            :label="t('placeholders.status')"
            :options="[
              {label: t('options.completed'), value: 'completed'},
              {label: t('options.incomplete'), value: 'incomplete'},
            ]"
          />
        </s-filter-group>

        <s-results-count :shown="totalFilteredAssignments" :total="totalAssignments" />
      </div>
    </search>
    <section class="@container flex flex-col gap-5">
      <s-placeholder v-if="filteredItems.length === 0">
        <template v-if="items.length">
          {{ t('search.noResults') }}
        </template>
        <template v-else>
          {{ t('page.noAssignments') }}
        </template>
      </s-placeholder>
      <ol v-else class="flex flex-col gap-4">
        <li v-for="item in filteredItems" :key="item.id">
          <student-assignment-card
            v-if="isAssignment(item)"
            :assignment="item"
            :grade="item.grade"
            :course="course"
          />
          <assignment-module-card
            v-else-if="isModule(item)"
            :module="item"
            :assignmentCount="item.assignments.length"
          >
            <template #badges>
              <s-badge
                :color="
                  getCompletedAssignments(item) < (item as any).assignments.length
                    ? 'gray'
                    : 'green'
                "
              >
                <s-icon
                  v-if="getCompletedAssignments(item) >= (item as any).assignments.length"
                  name="check-bold"
                  size="16"
                  class="opacity-70"
                />
                <s-icon v-else name="book-edit" size="16" class="text-gray-700 opacity-70" />
                <i18n-t keypath="xOfYAssignmentsCompleted">
                  <template v-slot:x>
                    <strong class="font-bold">
                      {{ getCompletedAssignments(item) }}
                    </strong>
                  </template>
                  <template v-slot:y>
                    <strong class="font-bold">
                      {{ (item as any).assignments.length }}
                    </strong>
                  </template>
                </i18n-t>
                <s-tooltip v-if="hasUnsubmittedDrafts(item)">
                  <template #tooltip>
                    {{ t('labels.draftWarning', {count: getUnsubmittedDraftsCount(item)}) }}
                  </template>
                  <s-icon name="alert" class="text-gray-700 opacity-70" size="16" />
                </s-tooltip>
              </s-badge>
            </template>
            <li v-for="assignment in (item as any).assignments" :key="assignment.id">
              <student-assignment-card
                :assignment="assignment"
                :grade="assignment.grade"
                :course="course"
              />
            </li>
          </assignment-module-card>
        </li>
      </ol>
    </section>
  </s-page-block>
</template>
<script setup lang="ts">
import {Head} from '@inertiajs/vue3';
import SPageHeader from '@/design-system/SPageHeader.vue';
import SPlaceholder from '@/design-system/SPlaceholder.vue';
import {Course} from '../../types/entities/course';
import SPageBlock from '../../design-system/SPageBlock.vue';
import StudentCourseAssignmentRowDto = App.DTOs.CourseAssignments.StudentCourseAssignmentRowDto;
import {useI18n} from 'vue-i18n';
import StudentAssignmentCard from './Index/StudentAssignmentCard.vue';
import {computed, reactive, watch} from 'vue';
import SInputField from '../../design-system/SInputField.vue';
import SBadge from '../../design-system/SBadge.vue';
import SIcon from '../../design-system/SIcon.vue';
import STooltip from '../../design-system/STooltip.vue';
import AssignmentModuleCard from './Index/AssignmentModuleCard.vue';
import SFilterGroup from '../../design-system/SFilterGroup.vue';
import SDateRangeFilter from '../../design-system/filters/SDateRangeFilter.vue';
import SSelectFilter from '../../design-system/filters/SSelectFilter.vue';
import {usePageParams} from '../../composables/usePageParams';
import {route} from 'ziggy-js';
import SResultsCount from '../../design-system/SResultsCount.vue';

type Module = {
  type: 'module';
  id: string;
  name: string;
  assignments: StudentCourseAssignmentRowDto[];
};

type Item = StudentCourseAssignmentRowDto | Module;

const {course, items} = defineProps<{
  course: Course;
  items: Item[];
}>();

const {t} = useI18n({
  useScope: 'local',
  inheritLocale: true,
});

type Status = 'incomplete' | 'completed';

type Form = {
  search?: string;
  startDate?: {start?: string | null; end?: string | null};
  dueDate?: {start?: string | null; end?: string | null};
  status?: Status | null;
};

const query = usePageParams<Form>({
  search: '',
  startDate: {start: null, end: null},
  dueDate: {start: null, end: null},
  status: null,
});

const form = reactive<Form>(query);

watch(form, function updateQueryString() {
  const _query: Partial<Form> = {};
  if (form.search) _query.search = form.search;
  if (form.status) _query.status = form.status;
  if (form.startDate?.start || form.startDate?.end) {
    _query.startDate = {start: form.startDate.start, end: form.startDate.end};
  }
  if (form.dueDate?.start || form.dueDate?.end) {
    _query.dueDate = {start: form.dueDate.start, end: form.dueDate.end};
  }

  const url = route('courses.assignments.index', {course: course.id, _query});

  window.history.replaceState({}, '', url);
});

const totalAssignments = computed(() => items.reduce(countAssignments, 0));
const totalFilteredAssignments = computed(() => filteredItems.value.reduce(countAssignments, 0));

const countAssignments = (acc: number, item: Item) => {
  if (isModule(item)) {
    return acc + item.assignments.length;
  }
  return acc + 1;
};

const filteredItems = computed<Item[]>(() => {
  if (
    !form.search &&
    !form.startDate?.start &&
    !form.startDate?.end &&
    !form.dueDate?.start &&
    !form.dueDate?.end &&
    !form.status
  ) {
    return items;
  }

  return items
    .map((item) => {
      if (isModule(item)) {
        let filteredModuleAssignments = item.assignments.filter(matchesSearch);

        if (!filteredModuleAssignments.length) {
          return null;
        }

        return {
          ...item,
          assignments: filteredModuleAssignments,
        } as Item;
      }
      return matchesSearch(item) ? item : null;
    })
    .filter(Boolean) as Item[];
});

function matchesSearch(assignment: StudentCourseAssignmentRowDto) {
  return (
    byName(assignment) && byStartDate(assignment) && byDueDate(assignment) && byStatus(assignment)
  );
}

function byName(assignment: StudentCourseAssignmentRowDto) {
  const search = form.search?.trim().toLowerCase();

  if (!search) {
    return true;
  }

  return assignment.name.toLowerCase().includes(search);
}

function byStartDate(assignment: StudentCourseAssignmentRowDto) {
  if (!form.startDate?.start && !form.startDate?.end) {
    return true;
  }
  const startDateString =
    assignment.extension?.newStartDate || assignment.courseLikeAssignmentsInfo.startDate;

  if (!startDateString) {
    return false;
  }

  const startDate = new Date(startDateString);
  const startFilter = form.startDate?.start ? new Date(form.startDate.start as string) : null;
  const endFilter = form.startDate?.end ? new Date(form.startDate.end as string) : null;

  if (startFilter && endFilter) {
    return startDate >= startFilter && startDate <= endFilter;
  } else if (startFilter) {
    return startDate >= startFilter;
  } else if (endFilter) {
    return startDate <= endFilter;
  }
  return true;
}

function byDueDate(assignment: StudentCourseAssignmentRowDto) {
  if (!form.dueDate?.start && !form.dueDate?.end) {
    return true;
  }
  const dueDateString =
    assignment.extension?.newDueDate || assignment.courseLikeAssignmentsInfo.dueDate;

  if (!dueDateString) {
    return false;
  }

  const startDate = new Date(dueDateString);
  const startFilter = form.dueDate?.start ? new Date(form.dueDate.start as string) : null;
  const endFilter = form.dueDate?.end ? new Date(form.dueDate.end as string) : null;

  if (startFilter && endFilter) {
    return startDate >= startFilter && startDate <= endFilter;
  } else if (startFilter) {
    return startDate >= startFilter;
  } else if (endFilter) {
    return startDate <= endFilter;
  }
  return true;
}

function byStatus(assignment: StudentCourseAssignmentRowDto) {
  if (!form.status) {
    return true;
  }
  return form.status === getAssignmentStatus(assignment);
}

function getAssignmentStatus(assignment: StudentCourseAssignmentRowDto): Status {
  const unclaimedExtensions =
    assignment.timer?.extensions?.filter((extension) => !extension.claimed) || [];

  let completed =
    assignment.numSubmissions >= assignment.numTasks ||
    (assignment.timer?.hasFinished && !unclaimedExtensions.length);

  if (completed) {
    return 'completed';
  } else {
    return 'incomplete';
  }
}

function isModule(item: Item): item is Module {
  return item.type === 'module';
}

function isAssignment(item: Item): item is StudentCourseAssignmentRowDto {
  return item.type !== 'module';
}

function getCompletedAssignments(module: Module): number {
  return (module.assignments || []).filter(
    (assignment: any) => assignment.numSubmissions >= assignment.numTasks
  ).length;
}

function hasUnsubmittedDrafts(module: Module): boolean {
  return (module.assignments || []).some((assignment: any) => assignment.numDrafts > 0);
}

function getUnsubmittedDraftsCount(module: Module): number {
  return (module.assignments || []).reduce(
    (acc: number, assignment: any) => acc + (assignment.numDrafts || 0),
    0
  );
}
</script>
<i18n>
{
  "en": {
    "page": {
      "title": "Assignments",
      "noAssignments": "You do not have any assignments at the moment."
    },
    "search": {
      "noResults": "No assignments found for your search.",
      "placeholder": "Search assignments by title"
    },
    "labels": {
      "draftWarning": "{count} assignments have unsubmitted drafts"
    },
    "xOfYAssignmentsCompleted": "{x} of {y} Assignments Completed",
    "count.results": "Showing {filtered} of {total} assignments",
    "options.completed": "Completed",
    "options.incomplete": "Incomplete",
    "placeholders.startDate": "Start Date",
    "placeholders.status": "Status",
    "placeholders.dueDate": "Due Date"
  },
  "fr": {
    "page": {
      "title": "Devoirs",
      "noAssignments": "Vous n'avez actuellement aucun devoir."
    },
    "search": {
      "noResults": "Aucun devoir trouvé pour votre recherche.",
      "placeholder": "Rechercher des devoirs par titre"
    },
    "labels": {
      "draftWarning": "{count} devoirs ont des brouillons non soumis"
    },
    "xOfYAssignmentsCompleted": "{x} de {y} devoirs terminés",
    "count.results": "Affichage de {filtered} sur {total} devoirs",
    "options.completed": "Terminé",
    "options.incomplete": "Incomplet",
    "placeholders.startDate": "Date de début",
    "placeholders.status": "Statut",
    "placeholders.dueDate": "Date d'échéance"
  }
}
</i18n>
